import React, { useState, useRef, useEffect } from 'react';

interface OptimizedVideoProps {
  src: string;
  poster?: string;
  className?: string;
  priority?: boolean;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  controls?: boolean;
  pauseOnHoverEnd?: boolean;
  onLoad?: () => void;
  onError?: () => void;
  style?: React.CSSProperties;
  objectFit?: 'cover' | 'contain' | 'fill' | 'scale-down' | 'none';
}

const OptimizedVideo: React.FC<OptimizedVideoProps> = ({
  src,
  poster,
  className = '',
  priority = false,
  autoPlay = false,
  muted = true,
  loop = false,
  controls = false,
  pauseOnHoverEnd = true,
  onLoad,
  onError,
  style,
  objectFit = 'cover',
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const [hasError, setHasError] = useState(false);
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (priority || isInView) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observerRef.current?.disconnect();
          }
        });
      },
      {
        rootMargin: '100px', // Start loading 100px before video comes into view
        threshold: 0.1,
      }
    );

    if (containerRef.current) {
      observerRef.current.observe(containerRef.current);
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, [priority, isInView]);

  const handleLoadedMetadata = () => {
    setIsLoaded(true);
    
    // Seek to first frame for thumbnail
    if (videoRef.current && !autoPlay) {
      videoRef.current.currentTime = 0.1;
    }
    
    onLoad?.();
  };

  const handleCanPlay = () => {
    // Hide placeholder when video is ready to play
    setShowPlaceholder(false);
  };

  const handlePlay = () => {
    // Ensure placeholder is hidden when playing
    setShowPlaceholder(false);
  };

  // Handle autoPlay changes with better error handling
  useEffect(() => {
    if (videoRef.current && isLoaded) {
      const video = videoRef.current;
      
      if (autoPlay) {
        // Ensure video is ready before playing
        if (video.readyState >= 3) { // HAVE_FUTURE_DATA
          const playPromise = video.play();
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                console.log('Video playing successfully');
                setShowPlaceholder(false);
              })
              .catch((error) => {
                console.log('Video autoplay failed, showing first frame:', error);
                // Even if autoplay fails, show the video (first frame)
                setShowPlaceholder(false);
              });
          }
        } else {
          // Wait for video to be ready
          const handleCanPlayThrough = () => {
            const playPromise = video.play();
            if (playPromise !== undefined) {
              playPromise
                .then(() => {
                  console.log('Video playing successfully after ready');
                  setShowPlaceholder(false);
                })
                .catch((error) => {
                  console.log('Video autoplay failed after ready:', error);
                  setShowPlaceholder(false);
                });
            }
            video.removeEventListener('canplaythrough', handleCanPlayThrough);
          };
          video.addEventListener('canplaythrough', handleCanPlayThrough);
        }
      } else if (!pauseOnHoverEnd) {
        // If pauseOnHoverEnd is false, pause but don't reset to beginning
        video.pause();
      } else {
        // Default behavior: pause and reset
        video.pause();
        video.currentTime = 0;
      }
    }
  }, [autoPlay, isLoaded, pauseOnHoverEnd]);

  const handleError = () => {
    setHasError(true);
    setShowPlaceholder(false);
    onError?.();
  };

  // Error state with cool design
  if (hasError) {
    return (
      <div 
        ref={containerRef}
        className={`relative overflow-hidden ${className}`}
        style={style}
      >
        {/* Gradient background */}
        <div className="w-full h-full bg-gradient-to-br from-red-900/20 to-dark-800 flex items-center justify-center">
          <svg className="w-12 h-12 text-red-400/60" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent"></div>
        
        {/* Error indicator */}
        <div className="absolute bottom-2 left-2 right-2">
          <div className="text-xs text-red-400/80 text-center">Video failed to load</div>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className={`relative overflow-hidden ${className}`}
      style={style}
    >
      {/* Cool placeholder while loading */}
      {showPlaceholder && (
        <div className="absolute inset-0">
          {poster ? (
            <img 
              src={poster} 
              alt="Video poster"
              className="w-full h-full object-cover"
              style={{ backgroundColor: 'transparent' }}
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-dark-700 to-dark-800 flex items-center justify-center">
              <svg className="w-12 h-12 text-dark-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          )}
          
          {/* Loading indicator */}
          {!isInView && (
            <div className="absolute bottom-2 left-2 right-2">
              <div className="h-2 bg-dark-600 rounded-full mb-1"></div>
              <div className="h-2 bg-dark-600 rounded-full w-3/4"></div>
            </div>
          )}
        </div>
      )}


      
      {/* Main video - only render when in view */}
      {isInView && (
        <video
          ref={videoRef}
          className={`w-full h-full object-${objectFit}`}
          poster=""
          autoPlay={autoPlay}
          muted={muted}
          loop={loop}
          controls={controls}
          playsInline={true} // Important for mobile smoothness
          preload={priority ? 'auto' : 'metadata'} // Better preloading for priority videos
          disablePictureInPicture={true} // Disable PiP for better performance
          onLoadedMetadata={handleLoadedMetadata}
          onCanPlay={handleCanPlay}
          onPlay={handlePlay}
          onError={handleError}
          style={{
            opacity: isLoaded && !showPlaceholder ? 1 : 0,
            transition: 'opacity 0.3s ease',
            backgroundColor: 'transparent',
            transform: 'translate3d(0, 0, 0)', // Enable hardware acceleration
            backfaceVisibility: 'hidden', // Optimize rendering
            willChange: 'transform, opacity', // Hint for GPU acceleration
            perspective: '1000px', // Force 3D context
            isolation: 'isolate', // Force GPU layer
            contain: 'layout style paint' as any, // Optimize rendering
            ...style // Allow custom styles to override
          }}
        >
          <source src={src} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      )}
    </div>
  );
};

export default OptimizedVideo;